import { useState } from "react";
import { ScanBar } from "./ScanBar";
import { ConfirmDialog } from "./ConfirmDialog";
import { getScanStages, normalizeScanStatus } from "../api";
import { useAppState } from "../AppStateContext";
import { useScanElapsed } from "./useScanElapsed";
import { useI18n } from "../i18n";

/**
 * Top-of-page scan strip used on every authenticated page. Renders the
 * ScanBar with a ticking count-up timer plus pause/cancel wired to AppState.
 * Renders nothing when there is no active scan.
 */
export function ScanStrip() {
  const { viewedScan, activeScanGraph, pauseScan, resumeScan, cancelScan, reload } = useAppState();
  const { t } = useI18n();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const hasScan = !!viewedScan;
  const status = normalizeScanStatus(viewedScan?.status);
  const elapsed = useScanElapsed(viewedScan);

  const handleToggle = async () => {
    if (!viewedScan) return;
    if (status === "running") await pauseScan(viewedScan.scan_id);
    else await resumeScan(viewedScan.scan_id);
  };

  const handleConfirmCancel = async () => {
    setConfirmCancel(false);
    await cancelScan(viewedScan?.scan_id);
    await reload();
  };

  if (!hasScan) return null;

  return (
    <>
      <ScanBar
        elapsed={elapsed}
        status={viewedScan!.status ?? "running"}
        stages={getScanStages(viewedScan, activeScanGraph)}
        onToggle={handleToggle}
        onDelete={() => setConfirmCancel(true)}
      />
      <ConfirmDialog
        open={confirmCancel}
        title={t("scan.confirmCancelTitle")}
        body={t("scan.confirmCancelBody")}
        confirmLabel={t("scan.confirmCancelLabel")}
        cancelLabel={t("common.cancel")}
        onConfirm={handleConfirmCancel}
        onClose={() => setConfirmCancel(false)}
      />
    </>
  );
}
